import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, ChevronDown, Menu, X } from 'lucide-react';
import '../../ui/Shared.css';
import './Topbar.css';
import { parseJwt } from '../../../utils/jwt';
import { useSelector } from 'react-redux';
import { readStoreProfileReadiness } from '../../../utils/storeProfileReadiness';

const MARKETING_NAV_ITEMS = [
  // { label: 'Pricing', path: '/pricing' },
  // { label: 'Blog', path: '/blog' },
  // { label: 'Docs', path: '/docs' },
  // { label: 'Changelog', path: '/changelog' },
  // { label: 'Community', path: '/community' }
];

const formatPlanLabel = (rawPlan) => {
  const value = String(rawPlan || '').trim();
  if (!value) {
    return 'Free';
  }
  return value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const DASHBOARD_MOBILE_LINKS = [
  { label: 'Dashboard', path: '/store' },
  { label: 'Store Profile', path: '/store/profile' },
  { label: 'Plan & Billings', path: '/store/billing' },
  { label: 'Upgrade Plan', path: '/store/upgrade' },
  { label: 'Settings', path: '/store/settings' }
];

const Topbar = ({
  role,
  authToken = '',
  showStoreMenu,
  onStoreLogout,
  variant = 'marketing',
  storeName = '',
  route = '/'
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [profileReadiness, setProfileReadiness] = useState(null);
  const menuRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const mobileCloseRef = useRef(null);
  const dropdownCloseTimerRef = useRef(null);
  const storeUuid = useSelector((state) => state.store.storeUuid);

  const isDashboardVariant = variant === 'dashboard';
  const resolvedStoreName = storeName || 'Store Dashboard';
  const resolvedPlanLabel = useMemo(() => {
    const claims = parseJwt(authToken);
    return formatPlanLabel(
      claims?.currentPlan
      || claims?.plan
      || claims?.subscriptionPlan
      || 'Free'
    );
  }, [authToken]);

  const dashboardSectionLabel = useMemo(() => {
    if (!route || !isDashboardVariant) {
      return 'Dashboard';
    }

    if (route.startsWith('/store/analytics')) {
      return 'Analytics';
    }
    if (route.startsWith('/store/appointments')) {
      return 'Appointments';
    }
    if (route.startsWith('/store/membership')) {
      return 'Memberships';
    }
    if (route.startsWith('/store/manage')) {
      return 'Manage';
    }
    if (route.startsWith('/store/profile')) {
      return 'Store Profile';
    }
    if (route.startsWith('/store/settings')) {
      return 'Settings';
    }
    if (route.startsWith('/store/billing')) {
      return 'Billing';
    }
    return 'Dashboard';
  }, [isDashboardVariant, route]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setProfileReadiness(readStoreProfileReadiness(storeUuid));
  }, [route, storeUuid]);

  useEffect(() => {
    const handleReadinessUpdated = (event) => {
      const nextStoreUuid = event?.detail?.storeUuid || storeUuid;
      if (!nextStoreUuid) {
        return;
      }
      setProfileReadiness(readStoreProfileReadiness(nextStoreUuid));
    };

    window.addEventListener('storeProfileReadinessUpdated', handleReadinessUpdated);
    return () => window.removeEventListener('storeProfileReadinessUpdated', handleReadinessUpdated);
  }, [storeUuid]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [route, isDashboardVariant]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return () => {};
    }
    if (mobileOpen) {
      document.body.classList.add('topbar-mobile-open');
    } else {
      document.body.classList.remove('topbar-mobile-open');
    }
    return () => {
      document.body.classList.remove('topbar-mobile-open');
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen || !mobilePanelRef.current) {
      return () => {};
    }

    const panel = mobilePanelRef.current;
    const focusable = panel.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    mobileCloseRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }

      if (event.key !== 'Tab' || focusable.length === 0) {
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      mobileToggleRef.current?.focus();
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isMenuClick = menuRef.current && menuRef.current.contains(target);
      const isMobileClick = (mobilePanelRef.current && mobilePanelRef.current.contains(target))
        || (mobileToggleRef.current && mobileToggleRef.current.contains(target));
      const isDropdownClick = target.closest('.nav-item-container') || target.closest('.mobile-nav-group');

      if (!isMenuClick) {
        setMenuOpen(false);
      }
      if (!isMobileClick) {
        setMobileOpen(false);
      }
      if (!isDropdownClick) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, true);
    return () => document.removeEventListener('pointerdown', handleClickOutside, true);
  }, []);

  useEffect(() => () => {
    if (dropdownCloseTimerRef.current) {
      window.clearTimeout(dropdownCloseTimerRef.current);
    }
  }, []);

  const cancelDropdownClose = () => {
    if (dropdownCloseTimerRef.current) {
      window.clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
  };

  const openDropdown = (label) => {
    cancelDropdownClose();
    setActiveDropdown(label);
  };

  const scheduleDropdownClose = () => {
    cancelDropdownClose();
    dropdownCloseTimerRef.current = window.setTimeout(() => {
      setActiveDropdown(null);
      dropdownCloseTimerRef.current = null;
    }, 180);
  };

  const handleMenuNavigate = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setMenuOpen(false);
    setMobileOpen(false);
    setActiveDropdown(null);
  };

  const navItems = useMemo(() => {
    if (isDashboardVariant) {
      return [];
    }
    return MARKETING_NAV_ITEMS;
  }, [isDashboardVariant]);

  return (
    <header className={`topbar ${isDashboardVariant ? 'dashboard' : 'marketing'}${isScrolled ? ' scrolled' : ''}`}>
      <div className="topbar-inner">
        <div className="brand">
          <button className="brand-mark" type="button" onClick={() => handleMenuNavigate(isDashboardVariant ? '/store' : '/')} aria-label="Go to home">
            <img className="brand-mark-image" src="/zopeo-icon.png?v=7" alt="" aria-hidden="true" />
          </button>
          <div>
            <button className="brand-title" type="button" onClick={() => handleMenuNavigate(isDashboardVariant ? '/store' : '/')}>
              Z<span className="brand-q">o</span>pe<span className="brand-q">o</span>
            </button>
            <div className="brand-subtitle">
            </div>
          </div>
        </div>

        {isDashboardVariant ? (
          <div className="topbar-dashboard-center">
            <div className="topbar-store-context" title={resolvedStoreName}>
              {dashboardSectionLabel !== 'Dashboard' && (
                <span className="topbar-store-context-eyebrow">{dashboardSectionLabel}</span>
              )}
              <span className="topbar-store-context-name">{resolvedStoreName}</span>
            </div>
          </div>
        ) : (
          <nav className="topnav" aria-label="Primary navigation">
            {navItems.map((item) => (
              <div
                key={item.label}
                className={`nav-item-container${item.dropdown ? ' has-dropdown' : ''}`}
                onMouseEnter={() => item.dropdown && openDropdown(item.label)}
                onMouseLeave={() => item.dropdown && scheduleDropdownClose()}
              >
                <button
                  className={`nav-link${item.hasCaret ? ' has-caret' : ''}${activeDropdown === item.label ? ' active' : ''}`}
                  type="button"
                  onClick={() => {
                    if (item.dropdown) {
                      setActiveDropdown(activeDropdown === item.label ? null : item.label);
                    } else if (item.path) {
                      handleMenuNavigate(item.path);
                    }
                  }}
                >
                  <span>{item.label}</span>
                  {item.hasCaret ? (
                    <ChevronDown className="nav-caret" size={12} aria-hidden="true" />
                  ) : null}
                </button>

                {item.dropdown && activeDropdown === item.label ? (
                  <div
                    className="nav-dropdown"
                    onMouseEnter={() => openDropdown(item.label)}
                    onMouseLeave={scheduleDropdownClose}
                  >
                    {Object.entries(item.dropdown).map(([category, links]) => (
                      <div key={category} className="dropdown-column">
                        <div className="dropdown-header">{category}</div>
                        <div className="dropdown-links">
                          {links.map((link) => (
                            <button
                              key={link}
                              className="dropdown-link"
                              type="button"
                              onClick={() => handleMenuNavigate(`/${link.toLowerCase().replace(/\s+/g, '-')}`)}
                            >
                              {link}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </nav>
        )}

        <div className="topbar-actions">
          {!isDashboardVariant ? (
            <button className="btn ghost sm" type="button" onClick={() => handleMenuNavigate('/support')}>
              Support
            </button>
          ) : null}

          {isDashboardVariant ? (
            <button className="topbar-icon-btn" type="button" aria-label="Notifications">
              <Bell size={18} aria-hidden="true" />
            </button>
          ) : null}

          {!isDashboardVariant && showStoreMenu ? (
            <button className="btn primary sm" type="button" onClick={() => handleMenuNavigate('/store')}>
              Dashboard
            </button>
          ) : null}

          {showStoreMenu ? (
            <div className="profile-menu" ref={menuRef}>
              <button className="profile-trigger" type="button" onClick={() => setMenuOpen((prev) => !prev)} aria-label="Open profile menu">
                <span className="profile-avatar-wrap">
                  <span className="profile-avatar" aria-hidden="true">
                    {(resolvedStoreName || 'S').trim().slice(0, 1).toUpperCase()}
                  </span>
                </span>
              </button>
              {profileReadiness?.completionLevel && profileReadiness.completionLevel !== 'HIGH' ? (
                <button
                  type="button"
                  className={`profile-warning-badge ${String(profileReadiness.completionLevel).toLowerCase()}`}
                  aria-label="Complete your store profile"
                  onClick={() => handleMenuNavigate('/store/profile')}
                >
                  !
                  <span className="profile-warning-tooltip">Complete your store profile</span>
                </button>
              ) : null}

              {menuOpen ? (
                <div className="profile-dropdown">
                  <div className="profile-header">
                    <div className="profile-avatar large" aria-hidden="true">{(resolvedStoreName || 'S').trim().slice(0, 1).toUpperCase()}</div>
                    <div>
                      <div className="profile-name">{resolvedStoreName}</div>
                      <div className="profile-plan">{resolvedPlanLabel}</div>
                    </div>
                  </div>
                  <div className="profile-divider" />
                  <button className="profile-item" type="button" onClick={() => handleMenuNavigate('/store/profile')}>Store Profile</button>
                  <button className="profile-item" type="button" onClick={() => handleMenuNavigate('/store/billing')}>Plan & Billings</button>
                  <button className="profile-item" type="button" onClick={() => handleMenuNavigate('/store/settings')}>Settings</button>
                  <div className="profile-divider" />
                  <button className="profile-item logout" type="button" onClick={() => { setMenuOpen(false); onStoreLogout(); }}>Logout</button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <button
          className="mobile-toggle touch-target"
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
          ref={mobileToggleRef}
        >
          <Menu size={18} aria-hidden="true" />
        </button>
      </div>

      <div className={`mobile-panel ${mobileOpen ? 'open' : ''}`} aria-hidden={!mobileOpen}>
        <button
          type="button"
          className="mobile-panel-backdrop"
          onClick={() => setMobileOpen(false)}
          tabIndex={mobileOpen ? 0 : -1}
          aria-label="Close mobile navigation"
        />
        <div className="mobile-panel-inner" ref={mobilePanelRef} role="dialog" aria-modal="true" aria-label="Mobile navigation">
          <div className="mobile-panel-header">
            <strong>Menu</strong>
            <button
              type="button"
              className="mobile-panel-close touch-target"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              ref={mobileCloseRef}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          {!isDashboardVariant ? (
            navItems.map((item) => (
              <div key={item.label} className="mobile-nav-group">
                <button
                  className={`mobile-link${item.hasCaret ? ' has-caret' : ''}`}
                  type="button"
                  onClick={() => {
                    if (item.dropdown) {
                      setActiveDropdown(activeDropdown === item.label ? null : item.label);
                    } else if (item.path) {
                      handleMenuNavigate(item.path);
                    }
                  }}
                >
                  {item.label}
                  {item.hasCaret ? (
                    <ChevronDown className={`nav-caret${activeDropdown === item.label ? ' open' : ''}`} size={12} aria-hidden="true" />
                  ) : null}
                </button>

                {item.dropdown && activeDropdown === item.label ? (
                  <div className="mobile-dropdown">
                    {Object.entries(item.dropdown).map(([category, links]) => (
                      <div key={category} className="mobile-dropdown-column">
                        <div className="mobile-dropdown-header">{category}</div>
                        {links.map((link) => (
                          <button
                            key={link}
                            className="mobile-dropdown-link"
                            type="button"
                            onClick={() => handleMenuNavigate(`/${link.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            {link}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))
          ) : null}

          {!isDashboardVariant ? (
            <div className="mobile-nav-group">
              <button className="mobile-link" type="button" onClick={() => handleMenuNavigate('/support')}>Support</button>
            </div>
          ) : null}

          {showStoreMenu ? (
            <>
              <div className="mobile-panel-section-title">Account</div>
              {DASHBOARD_MOBILE_LINKS.map((item) => (
                <button
                  key={item.path}
                  className={`mobile-link touch-target${route === item.path ? ' active' : ''}`}
                  type="button"
                  onClick={() => handleMenuNavigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
              <button className="mobile-link logout touch-target" type="button" onClick={onStoreLogout}>Logout</button>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
